/**
 * Per-tab selectable agent target for direct (browser-signed) auth.
 *
 * One deployed web app can drive multiple agents: each tab/window picks the
 * agent URL at login. The choice is kept in sessionStorage — scoped to the
 * browsing context (tab/window), NOT shared across tabs like localStorage —
 * so two tabs can target different agents at once without crossing. It
 * survives a reload of that tab and defaults to the build-time env value
 * (constants.API_BASE_URL, which in direct mode resolves to
 * VITE_AGENT_DIRECT_URL). Only meaningful in direct mode; the gateway path has
 * a fixed upstream. See this.i wqz7k3mnbp.
 */

import constants from '@/config/constant';

const STORAGE_KEY = 'kagent.directAuth.agentUrl';

/** The base URL the URL builders bake in (env default). */
export function defaultAgentTargetUrl(): string {
  return constants.API_BASE_URL;
}

/** Active agent target: the sessionStorage override, else the env default. */
export function getAgentTargetUrl(): string {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) return stored;
  } catch {
    // sessionStorage unavailable (private mode, SSR) — fall back to default.
  }
  return defaultAgentTargetUrl();
}

/**
 * Persist the chosen agent target. Storing the env default (or an empty value)
 * clears the override so the app tracks the env again.
 */
export function setAgentTargetUrl(url: string): void {
  const trimmed = url.trim();
  try {
    if (!trimmed || trimmed === defaultAgentTargetUrl()) {
      sessionStorage.removeItem(STORAGE_KEY);
    } else {
      sessionStorage.setItem(STORAGE_KEY, trimmed);
    }
  } catch {
    // Best effort; ignore storage failures.
  }
}

/** Free-form validation: any well-formed http(s) URL is accepted. */
export function isValidAgentUrl(url: string): boolean {
  try {
    const u = new URL(url.trim());
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

/** Drop trailing slashes so an origin + path join never yields '//'. */
function stripTrailingSlash(url: string): string {
  return url.replace(/\/+$/, '');
}

/**
 * Re-point a URL built against the env default at the active target. URL
 * builders (shim-api, wallet) prefix paths with constants.API_BASE_URL; this
 * swaps that origin for the chosen agent. Only the origin changes — the signed
 * @path/@query are untouched, so request signing is unaffected.
 *
 * Both the env default and the per-tab override are normalized (trailing
 * slashes stripped) before joining, so a pasted "http://agent:9079/" cannot
 * produce a malformed "http://agent:9079//v1/shim/..." path.
 */
export function applyAgentTarget(url: string): string {
  const rawTarget = getAgentTargetUrl();
  const rawBase = defaultAgentTargetUrl();
  if (rawTarget === rawBase) return url;
  if (!url.startsWith(rawBase)) return url;
  let rest = url.slice(rawBase.length);
  if (rest && !rest.startsWith('/')) rest = `/${rest}`;
  return `${stripTrailingSlash(rawTarget)}${rest}`;
}
